import React, { useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Loader2, FileText, Plus, Trash2, Calculator, Check, Package, Upload, Paperclip, X, Eye } from "lucide-react";
import PartnerSelect from "@/components/shared/PartnerSelect";

const categorias = [
  { value: "semente", label: "Sementes" },
  { value: "fertilizante", label: "Fertilizantes" },
  { value: "defensivo", label: "Defensivos" },
  { value: "combustivel", label: "Combustível" },
  { value: "racao", label: "Ração" },
  { value: "medicamento", label: "Medicamentos" },
  { value: "outro", label: "Outros" }
];

export default function EntradaNotaFiscal({ insumos, propriedadeId }) {
  const { data: unidades = [] } = useQuery({
    queryKey: ['unidades_medida'],
    queryFn: () => agrogestao.entities.UnidadeMedida.filter({}, 'sigla')
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nfData, setNfData] = useState({
    numero_nf: "",
    data_emissao: new Date().toISOString().split('T')[0],
    itens: [],
    pdf_url: "",
    fornecedor: ""
  });
  const [novoItem, setNovoItem] = useState({
    insumo_id: "",
    nome: "",
    categoria: "semente",
    unidade: "",
    quantidade: "",
    valor_unitario: "",
    lote: "",
    validade: "",
    isNew: false
  });
  const [isUploadingPDF, setIsUploadingPDF] = useState(false);
  const pdfInputRef = useRef(null);

  const queryClient = useQueryClient();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };



  // Upload do PDF da NF
  const handlePDFUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingPDF(true);
    try {
      const { file_url } = await agrogestao.integrations.Core.UploadFile({ file });
      setNfData(prev => ({ ...prev, pdf_url: file_url }));
    } catch (error) {
      console.error("Erro no upload:", error);
    } finally {
      setIsUploadingPDF(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const processarNFMutation = useMutation({
    mutationFn: async (data) => {
      const u = await agrogestao.auth.me();
      const dataHoje = new Date().toISOString().split('T')[0];

      for (const item of data.itens) {
        const valorTotal = item.quantidade * item.valor_unitario;
        const fornecedorItem = data.fornecedor || "";

        if (item.insumo_id && !item.isNew) {
          // Atualizar insumo existente - calcular custo médio
          const insumoExistente = insumos.find(i => i.id === item.insumo_id);
          const qtdAnterior = insumoExistente?.quantidade_estoque || 0;
          const custoAnterior = insumoExistente?.custo_unitario || 0;
          const valorAnterior = qtdAnterior * custoAnterior;

          const novaQtd = qtdAnterior + item.quantidade;
          const custoMedio = novaQtd > 0 ? (valorAnterior + valorTotal) / novaQtd : item.valor_unitario;

          await agrogestao.entities.Insumo.update(item.insumo_id, {
            quantidade_estoque: novaQtd,
            custo_unitario: custoMedio,
            lote: item.lote || insumoExistente?.lote,
            validade: item.validade || insumoExistente?.validade,
            fornecedor: fornecedorItem || insumoExistente?.fornecedor,
            nota_fiscal: data.numero_nf
          });

          await agrogestao.entities.MovimentacaoInsumo.create({
            propriedade_id: propriedadeId,
            insumo_id: item.insumo_id,
            tipo: "entrada",
            quantidade: item.quantidade,
            data: dataHoje,
            custo_total: valorTotal,
            observacao: `NF: ${data.numero_nf}`
          });
        } else {
          const novoInsumo = await agrogestao.entities.Insumo.create({
            propriedade_id: propriedadeId,
            nome: item.nome,
            categoria: item.categoria,
            unidade: item.unidade,
            quantidade_estoque: item.quantidade || 0,
            custo_unitario: item.valor_unitario || 0,
            fornecedor: fornecedorItem || null,
            lote: item.lote || null,
            validade: item.validade || null,
            nota_fiscal: data.numero_nf || null,
            created_by: u?.email || null
          });

          await agrogestao.entities.MovimentacaoInsumo.create({
            propriedade_id: propriedadeId,
            insumo_id: novoInsumo.id,
            tipo: "entrada",
            quantidade: item.quantidade,
            data: dataHoje,
            custo_total: valorTotal,
            observacao: `NF: ${data.numero_nf} - Primeiro cadastro`
          });
        }

        // Criar Conta a Pagar com anexo do PDF
        await agrogestao.entities.ContaPagar.create({
          propriedade_id: propriedadeId,
          descricao: `NF ${data.numero_nf}: ${item.nome || insumos.find(i => i.id === item.insumo_id)?.nome} (${item.quantidade} un)`,
          valor: valorTotal,
          data_vencimento: data.data_emissao || dataHoje,
          categoria: "insumos",
          fornecedor: fornecedorItem,
          status: "pendente",
          observacoes: `Lote: ${item.lote || '-'}${data.pdf_url ? ` | PDF: ${data.pdf_url}` : ''}`
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insumos'] });
      queryClient.invalidateQueries({ queryKey: ['movimentacoes'] });
      queryClient.invalidateQueries({ queryKey: ['contas_pagar'] });
      setDialogOpen(false);
      resetForm();
    }
  });

  const resetForm = () => {
    setNfData({
      numero_nf: "",
      data_emissao: new Date().toISOString().split('T')[0],
      itens: [],
      pdf_url: "",
      fornecedor: ""
    });
    setNovoItem({
      insumo_id: "",
      nome: "",
      categoria: "semente",
      unidade: "",
      quantidade: "",
      valor_unitario: "",
      lote: "",
      validade: "",
      isNew: false
    });
  };

  const addItem = () => {
    if (!novoItem.quantidade || !novoItem.valor_unitario) return;
    if (!novoItem.insumo_id && !novoItem.nome) return;

    const itemToAdd = {
      ...novoItem,
      quantidade: parseFloat(novoItem.quantidade),
      valor_unitario: parseFloat(novoItem.valor_unitario),
      valorTotal: parseFloat(novoItem.quantidade) * parseFloat(novoItem.valor_unitario)
    };

    if (novoItem.insumo_id && !novoItem.isNew) {
      const insumoSelecionado = insumos.find(i => i.id === novoItem.insumo_id);
      itemToAdd.nome = insumoSelecionado?.nome;
      itemToAdd.unidade = insumoSelecionado?.unidade;
    }

    setNfData(prev => ({ ...prev, itens: [...prev.itens, itemToAdd] }));
    setNovoItem({
      insumo_id: "",
      nome: "",
      categoria: "semente",
      unidade: "",
      quantidade: "",
      valor_unitario: "",
      lote: "",
      validade: "",
      isNew: false
    });
  };

  const removeItem = (index) => {
    setNfData(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setNfData(prev => ({
      ...prev,
      itens: prev.itens.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        if (field === 'quantidade' || field === 'valor_unitario') {
          updated.valorTotal = (parseFloat(updated.quantidade) || 0) * (parseFloat(updated.valor_unitario) || 0);
        }
        return updated;
      })
    }));
  };

  const totalNF = nfData.itens.reduce((acc, i) => acc + (i.valorTotal || 0), 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (nfData.itens.length === 0) return;
    processarNFMutation.mutate(nfData);
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button className="bg-indigo-600 hover:bg-indigo-700 gap-2">
          <FileText className="w-5 h-5" />
          Entrada por NF
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Entrada de Estoque por Nota Fiscal
          </DialogTitle>
          <DialogDescription>
            Registre entrada de estoque via nota fiscal
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          {/* Anexar PDF */}
          <div className="flex items-center gap-3">
            <input
              ref={pdfInputRef}
              type="file"
              accept=".pdf"
              onChange={handlePDFUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => pdfInputRef.current?.click()}
              disabled={isUploadingPDF}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
            >
              {isUploadingPDF ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Paperclip className="w-4 h-4 mr-2" />
                  Anexar PDF da NF
                </>
              )}
            </Button>

            {nfData.pdf_url && (
              <div className="flex items-center gap-2 p-2 bg-green-100 rounded-lg text-green-700 text-sm">
                <Paperclip className="w-4 h-4" />
                <span className="truncate">PDF anexado</span>
                <a href={nfData.pdf_url} target="_blank" rel="noopener noreferrer">
                  <Button type="button" variant="ghost" size="sm" className="text-green-700">
                    <Eye className="w-4 h-4" />
                  </Button>
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-500"
                  onClick={() => setNfData(prev => ({ ...prev, pdf_url: "" }))}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Dados da NF */}
          <Card className="bg-stone-50 border-stone-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-stone-700">Dados da Nota Fiscal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número NF *</Label>
                  <Input
                    placeholder="Ex: 123456"
                    value={nfData.numero_nf}
                    onChange={(e) => setNfData({ ...nfData, numero_nf: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Emissão</Label>
                  <Input
                    type="date"
                    value={nfData.data_emissao}
                    onChange={(e) => setNfData({ ...nfData, data_emissao: e.target.value })}
                  />
                </div>
              </div>
              <PartnerSelect
                tipo="fornecedor"
                value={nfData.fornecedor}
                onChange={(v) => setNfData({ ...nfData, fornecedor: v })}
              />
            </CardContent>
          </Card>

          {/* Adicionar Item */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Adicionar Item
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNovoItem(p => ({ ...p, isNew: false, insumo_id: "", nome: "" }))}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all border ${!novoItem.isNew ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-stone-200'
                    }`}
                >
                  Insumo Existente
                </button>
                <button
                  type="button"
                  onClick={() => setNovoItem(p => ({ ...p, isNew: true, insumo_id: "" }))}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all border ${novoItem.isNew ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-stone-200'
                    }`}
                >
                  Novo Insumo
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {!novoItem.isNew ? (
                  <div className="space-y-2 col-span-2">
                    <Label>Selecionar Insumo *</Label>
                    <Select
                      value={novoItem.insumo_id}
                      onValueChange={(v) => setNovoItem({ ...novoItem, insumo_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {insumos.map(i => (
                          <SelectItem key={i.id} value={i.id}>
                            {i.nome} ({i.quantidade_estoque} {i.unidade})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Nome do Insumo *</Label>
                      <Input
                        placeholder="Ex: Fertilizante NPK"
                        value={novoItem.nome}
                        onChange={(e) => setNovoItem({ ...novoItem, nome: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Categoria</Label>
                      <Select
                        value={novoItem.categoria}
                        onValueChange={(v) => setNovoItem({ ...novoItem, categoria: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categorias.map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Unidade *</Label>
                      <Select
                        value={novoItem.unidade}
                        onValueChange={(v) => setNovoItem({ ...novoItem, unidade: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {unidades.map(u => (
                            <SelectItem key={u.id} value={u.sigla}>{u.sigla} - {u.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}



                <div className="space-y-2">
                  <Label>Quantidade *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={novoItem.quantidade}
                    onChange={(e) => setNovoItem({ ...novoItem, quantidade: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Valor Unitário (R$) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    value={novoItem.valor_unitario}
                    onChange={(e) => setNovoItem({ ...novoItem, valor_unitario: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lote</Label>
                  <Input
                    placeholder="Número do lote"
                    value={novoItem.lote}
                    onChange={(e) => setNovoItem({ ...novoItem, lote: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Validade</Label>
                  <Input
                    type="date"
                    value={novoItem.validade}
                    onChange={(e) => setNovoItem({ ...novoItem, validade: e.target.value })}
                  />
                </div>
              </div>

              {novoItem.quantidade && novoItem.valor_unitario && (
                <div className="p-3 bg-blue-100 rounded-lg flex items-center justify-between">
                  <span className="text-blue-700 font-medium flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Subtotal do item:
                  </span>
                  <span className="text-blue-800 font-bold">
                    {formatCurrency(parseFloat(novoItem.quantidade) * parseFloat(novoItem.valor_unitario))}
                  </span>
                </div>
              )}

              <Button
                type="button"
                onClick={addItem}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={!novoItem.quantidade || !novoItem.valor_unitario || (!novoItem.insumo_id && !novoItem.nome)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Item
              </Button>
            </CardContent>
          </Card>

          {/* Itens Adicionados */}
          {nfData.itens.length > 0 && (
            <Card className="bg-white border-stone-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-stone-700 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Itens da Nota ({nfData.itens.length})
                    {fornecedoresUnicos.length > 1 && (
                      <Badge variant="outline" className="text-xs">
                        {fornecedoresUnicos.length} fornecedores
                      </Badge>
                    )}
                  </span>
                  <Badge className="bg-green-100 text-green-700">Total: {formatCurrency(totalNF)}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                {nfData.itens.map((item, idx) => (
                  <div key={idx} className="p-3 bg-stone-50 rounded-lg space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Input
                          value={item.nome}
                          onChange={(e) => updateItem(idx, 'nome', e.target.value)}
                          className="font-medium text-stone-800 mb-1 h-8"
                          placeholder="Nome do item"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.quantidade}
                            onChange={(e) => updateItem(idx, 'quantidade', parseFloat(e.target.value) || 0)}
                            className="h-7 text-xs"
                            placeholder="Qtd"
                          />
                          <Input
                            value={item.unidade}
                            onChange={(e) => updateItem(idx, 'unidade', e.target.value)}
                            className="h-7 text-xs"
                            placeholder="Un"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            value={item.valor_unitario}
                            onChange={(e) => updateItem(idx, 'valor_unitario', parseFloat(e.target.value) || 0)}
                            className="h-7 text-xs"
                            placeholder="R$ Unit"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-3">
                        <span className="font-bold text-stone-800">{formatCurrency(item.valorTotal)}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 h-6 w-6 p-0"
                          onClick={() => removeItem(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Botões */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processarNFMutation.isPending || nfData.itens.length === 0}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {processarNFMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Processar NF ({formatCurrency(totalNF)})
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
